import React, { useState } from "react";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { GumastaListResponse } from "src/models/Gumasta.model";
import { useNavigate } from "react-router-dom";
import GumastaPendingApplicationListing from "./GumastaPendingApplicationListing";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import {
  useChangeGumastaStatusMutation,
  useGetGumastaPendingApplicationsQuery,
} from "src/services/GumastaService";
import {
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/GumastaApplication/GumastaPendingApplicationSlice";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/toaster/showToast";
import { formatDateAndTime } from "src/utils/dateAndTime";
import { getColumns } from "src/utils/auth/getColumns";
import AuthHOC from "src/userAccess/AuthHOC";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";


const paramList = [
  "_id",
  "propritorName",
  "adhaarNumber",
  "mobileNumber",
  "email",
  "adhaarPhotoUrl",
  "firmName",
  "firmAddress",
  "propritorPhotoUrl",
  "shopOfficePhotoUrl",
  "addressProofPhotoUrl",
  "otherDocuments",
  "state",
  "district",
  "distributorCode",
  "appliedBy",
  "appliedAs",
  "txnId",
  "payementDetails",
  "srn",
  "appliedFrom",
  "version",
  "acknowledgementNumber",
  "status",
  "assignedTo",
  "assignedBy",
  "createdAt",
  "updatedAt",
];

const GumastaPendingApplicationListingWrapper = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  // Slice
  const GumastaPendingApplicationState: any = useSelector(
    (state: RootState) => state.GumastaPendingApplication
  );
  const { page, rowsPerPage, items, searchValue, filterBy } =
    GumastaPendingApplicationState;
  const [changeStatus , changeStatusInfo] = useChangeGumastaStatusMutation();
  const [applicationId, setApplicationId] = useState("");
  // Get Data Query
  const { data, isFetching, isLoading } = useGetGumastaPendingApplicationsQuery(
    {
      limit: rowsPerPage,
      searchValue: searchValue,
      params: paramList,
      page: page,
      filterBy: filterBy,
      dateFilter: {
        start_date: "",
        end_date: "",
        dateFilterKey: "",
      },
      orderBy: "createdDateToShow",
      orderByValue: 1,
      isPaginationRequired: true,
    }
  );

  // Setting Retailer data
  React.useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const columns: columnTypes[] = [
    {
      hidden :!AuthHOC({
        resultType :"BOOLEAN",
        forApplicationStatus :true,
        applicationName:"GUMASTA",
        applicationStatus :"IN_PROGRESS"
      }),
      noAuthRequired: true,
      field: "",
      headerName: "Move To",
      flex: "flex-[1_1_0%]",
     
      renderCell: (row: GumastaListResponse) => (
        <div className="py-2">
           <ATMLoadingButton
            loadingText="Processing..."
            className="bg-green-400 w-24 p-0 py-[6px]"
            type="button"
            isLoading={applicationId === row?._id && changeStatusInfo?.isLoading}
            disabled={applicationId === row?._id && changeStatusInfo?.isLoading}
            onClick={(e) => {
              e.stopPropagation();
              showConfirmationDialog({
                title: "Heads Up",
                text: "Are you sure want to move Application to In Progress ?",
                icon: "question",
                showCancelButton: true,
                next: (result) => {
                  if (result.isConfirmed) { 
                    setApplicationId(row?._id);
                    const formData = new FormData();
                    formData.append("requestedStatus", "IN_PROGRESS");
                    changeStatus({
                      id: row._id,
                      body: formData,
                    }).then((res: any) => {
                      if (res.error) {
                        showToast("error", res.error.data?.message);
                      } else {
                        if (res.data?.status) {
                          showToast("success", res.data?.message);
                          navigate(`/gumasta/PENDING`);
                        } else {
                          showToast("error", res.data?.message);
                        }
                      }
                    });
                  }
                },
              });
            }}
          >
            In Progress
          </ATMLoadingButton>
        </div>
      ),
    },

    {
      field: "createdDateToShow",
      headerName: "Date - Time",
      flex: "flex-[1_1_0%]",
      renderCell: (row: GumastaListResponse) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: GumastaListResponse) => {
        return (
          <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
            {" "}
            {row.srn}{" "}
          </span>
        );
      },
    },
    {
      field: "propritorName",
      headerName: "Proprietor Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: GumastaListResponse) => (
        <span> {row.propritorName} </span>
      ),
    },

    {
      field: "adhaarNumber",
      headerName: "Aadhar Number",
      flex: "flex-[1_1_0%]",
      renderCell: (row: GumastaListResponse) => (
        <span> {row.adhaarNumber} </span>
      ),
    },
    {
      field: "firmName",
      headerName: "Firm Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: GumastaListResponse) => {
        return <span> {row.firmName} </span>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1_1_0%]",
      renderCell: (row: GumastaListResponse) => {
        return <span> {row.email} </span>;
      },
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
    },

    {
      field: "state",
      headerName: "State",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: GumastaListResponse) => {
        return <span> {row.state} </span>;
      },
    },
    {
      field: "district",
      headerName: "District",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: GumastaListResponse) => {
        return <span> {row.district} </span>;
      },
    },
  ];
  return (
    <>
      <GumastaPendingApplicationListing
        columns={getColumns(columns, "GUMASTA_APPLICATIONS")}
        rows={items}
      />
    </>
  );
};

export default GumastaPendingApplicationListingWrapper;
